import type { FC, MouseEvent } from 'react';

import { IconButton } from '@/lib/base/buttons/button-utility';
import { cx } from '@/utils/cx';

/**
 * Icon side-affordance for a trigger card's header row — the ONE
 * implementation of the compact icon button both trigger cards use
 * (`CrmEventTriggerPreview`'s "View thread"/"Open in CRM" and
 * `EmailTriggerCard`'s "View thread"), so the two cards cannot drift apart
 * visually.
 *
 * Overlay-trigger-pattern requirements baked in:
 * - `pointer-events-auto` — the button sits in a `pointer-events-none`
 *   content layer above the card's absolutely-positioned disclosure
 *   trigger, so it must re-enable pointer events to catch its own clicks
 *   instead of toggling the disclosure.
 * - `transition-none` — with that layering, even a short colour transition
 *   reads as a delay between the cursor entering the button and the hover
 *   bg appearing.
 *
 * `isPressed` renders the brand-tint open state (`aria-pressed` for
 * assistive tech), used while the record/thread this button opens is on the
 * slideout stack.
 */
export function TriggerCardIconButton({
  icon,
  label,
  isPressed,
  onClick,
  className,
}: {
  icon: FC<{ className?: string }>;
  /** Accessible name — the button renders no visible text. */
  label: string;
  /** Open-state highlight + `aria-pressed`. */
  isPressed: boolean;
  onClick: (e: MouseEvent) => void;
  className?: string;
}) {
  return (
    <IconButton
      size='xs'
      color='tertiary'
      icon={icon}
      aria-label={label}
      aria-pressed={isPressed}
      onClick={onClick}
      className={cx(
        'pointer-events-auto rounded p-1 text-current transition-none hover:text-current',
        isPressed
          ? 'bg-utility-brand-50 text-utility-brand-600 hover:bg-utility-brand-50 hover:text-utility-brand-600'
          : 'hover:bg-tertiary',
        className
      )}
    />
  );
}
